import { useState, useEffect } from 'react'
import CalendarExport from './CalendarExport'
import { supabase } from '../supabase'

function Section({ title, icon, filled, children, onEdit }) {
  const [expanded, setExpanded] = useState(false)

  function toggle() {
    if (!filled) return // don't expand if not filled
    setExpanded(e => !e)
  }

  return (
    <div className="card" style={{ marginBottom: '0.75rem', padding: '1rem', opacity: filled ? 1 : 0.5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: filled ? 'pointer' : 'default' }} onClick={toggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.1rem' }}>{icon}</span>
          <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#1A1A1A' }}>{title}</span>
          {filled
            ? <span className="badge badge-green">✓ Complete</span>
            : <span className="badge badge-gray">Not started yet</span>
          }
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          {filled && onEdit && (
            <button onClick={e => { e.stopPropagation(); onEdit() }} style={{ fontSize: '0.72rem', color: '#00712D', background: 'none', border: '1px solid #00712D', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer' }}>Edit</button>
          )}
          {filled && <span style={{ color: '#aaa', fontSize: '0.9rem' }}>{expanded ? '↑' : '↓'}</span>}
          {!filled && onEdit && (
            <button onClick={e => { e.stopPropagation(); onEdit() }} style={{ fontSize: '0.72rem', color: '#555', background: 'none', border: '1px solid #e8e8e8', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer' }}>Start →</button>
          )}
        </div>
      </div>
      {expanded && filled && <div style={{ marginTop: '0.85rem', paddingTop: '0.85rem', borderTop: '1px solid #f0f0f0' }}>{children}</div>}
    </div>
  )
}

function DataField({ label, value }) {
  if (!value) return null
  return (
    <div style={{ marginBottom: '0.6rem' }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>{label}</div>
      <div style={{ fontSize: '0.85rem', color: '#1A1A1A', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{value}</div>
    </div>
  )
}

export default function MySearch({ session, profile, onNavigate, onHired }) {
  const [pipeline, setPipeline] = useState([])
  const [hotTen, setHotTen] = useState([])
  const [loading, setLoading] = useState(true)
  const [showHired, setShowHired] = useState(false)
  const [quickAdd, setQuickAdd] = useState(false)
  const [qForm, setQForm] = useState({ company: '', role: '', source: 'LinkedIn' })

  useEffect(() => { loadAll() }, [])

  async function loadAll() {
    setLoading(true)
    const [p, h] = await Promise.all([
      supabase.from('pipeline').select('*').eq('user_id', session.user.id).order('created_at', { ascending: false }),
      supabase.from('hot_ten').select('*').eq('user_id', session.user.id).order('created_at'),
    ])
    setPipeline(p.data || [])
    setHotTen(h.data || [])
    setLoading(false)
  }

  async function quickAddPipeline() {
    if (!qForm.company || !qForm.role) return
    await supabase.from('pipeline').insert({ user_id: session.user.id, ...qForm, stage: 'identified', referral_status: 'none', date_added: new Date().toISOString() })
    setQForm({ company: '', role: '', source: 'LinkedIn' })
    setQuickAdd(false)
    loadAll()
  }

  const activePipeline = pipeline.filter(p => p.stage !== 'rejected')
  const hotTenActivated = hotTen.filter(h => h.status === 'activated').length

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 className="section-title" style={{ margin: 0 }}>My Search</h2>
        <button onClick={() => setShowHired(true)} style={{ fontSize: '0.75rem', background: '#00712D', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: 600 }}>
          🎉 I got hired!
        </button>
      </div>

      {/* Quick stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { label: 'Active', value: activePipeline.length, sub: 'pipeline' },
          { label: 'Activated', value: `${hotTenActivated}/10`, sub: 'Hot Ten' },
          { label: 'Target', value: profile?.target_companies ? profile.target_companies.split('\n').filter(Boolean).length : 0, sub: 'companies' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '0.75rem', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: 700, color: '#00712D', lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: '0.62rem', color: '#aaa', marginTop: '0.2rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label} {s.sub}</div>
          </div>
        ))}
      </div>

      {/* Quick access tools */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { label: '📓 Journal', tab: 'journal' },
          { label: '📄 Resume', tab: 'resume' },
          { label: '⭐ Progress', tab: 'progress' },
          { label: '📹 Videos', tab: 'resources' },
        ].map(item => (
          <button key={item.tab} onClick={() => onNavigate(item.tab)} style={{ padding: '0.65rem 0.35rem', border: '1px solid #e8e8e8', borderRadius: '8px', background: '#fff', fontSize: '0.72rem', fontWeight: 600, color: '#555', cursor: 'pointer', textAlign: 'center' }}>
            {item.label}
          </button>
        ))}
      </div>

      {/* Workbook sections */}
      <Section title="Who I Am" icon="👤" filled={!!profile?.what_you_love} onEdit={() => onNavigate('day1')}>
        <DataField label="What energizes me" value={profile?.what_you_love} />
        <DataField label="What I'm genuinely great at" value={profile?.what_youre_good_at} />
        <DataField label="What impact I want" value={profile?.what_world_needs} />
        <DataField label="Start / Stop / Keep" value={profile?.start_stop_keep} />
      </Section>

      <Section title="My Job Spec" icon="🎯" filled={!!profile?.target_titles && !!profile?.minimum_salary} onEdit={() => onNavigate('day2')}>
        <DataField label="Target titles" value={profile?.target_titles} />
        <DataField label="Ideal salary" value={profile?.ideal_salary} />
        <DataField label="Minimum salary" value={profile?.minimum_salary} />
        <DataField label="Work format" value={profile?.work_format} />
        <DataField label="Company size" value={profile?.company_size} />
        <DataField label="Non-negotiables" value={profile?.non_negotiables} />
        <DataField label="Deal-breakers" value={profile?.deal_breakers} />
        <DataField label="Nice to haves" value={profile?.nice_to_haves} />
      </Section>

      <Section title="Pipeline" icon="🎯" filled={activePipeline.length > 0} onEdit={() => onNavigate('pipeline')}>
        {/* Quick add */}
        {quickAdd ? (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', marginBottom: '0.4rem' }}>
              <input className="input-field" placeholder="Company" value={qForm.company} onChange={e => setQForm({ ...qForm, company: e.target.value })} style={{ fontSize: '0.82rem' }} />
              <input className="input-field" placeholder="Role" value={qForm.role} onChange={e => setQForm({ ...qForm, role: e.target.value })} style={{ fontSize: '0.82rem' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button className="btn-primary" onClick={quickAddPipeline} disabled={!qForm.company || !qForm.role} style={{ flex: 1, padding: '0.55rem', fontSize: '0.78rem' }}>Add</button>
              <button className="btn-ghost" onClick={() => setQuickAdd(false)} style={{ fontSize: '0.78rem' }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setQuickAdd(true)} style={{ width: '100%', padding: '0.55rem', border: '1px dashed #e8e8e8', borderRadius: '6px', background: '#fff', color: '#555', fontSize: '0.78rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
            + Quick add opportunity
          </button>
        )}

        {activePipeline.length === 0 ? (
          <p style={{ color: '#aaa', fontSize: '0.82rem', fontStyle: 'italic' }}>No active opportunities yet. Add your first role above or in the Pipeline tab.</p>
        ) : (
          activePipeline.slice(0, 5).map(item => (
            <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #f5f5f5' }}>
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{item.company}</div>
                <div style={{ fontSize: '0.72rem', color: '#888' }}>{item.role}</div>
              </div>
              <span style={{ fontSize: '0.68rem', color: '#888', fontWeight: 600 }}>{item.stage}</span>
            </div>
          ))
        )}
        {activePipeline.length > 5 && (
          <button onClick={() => onNavigate('pipeline')} style={{ width: '100%', padding: '0.5rem', background: 'none', border: 'none', color: '#00712D', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', marginTop: '0.4rem' }}>
            View all {activePipeline.length} opportunities →
          </button>
        )}
      </Section>

      <Section title="Hot Ten" icon="🔥" filled={hotTen.length >= 5} onEdit={() => onNavigate('network')}>
        {hotTen.length === 0 ? (
          <p style={{ color: '#aaa', fontSize: '0.82rem', fontStyle: 'italic' }}>Not started yet. Day 5 walks you through building your Hot Ten.</p>
        ) : (
          hotTen.map((c, i) => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', borderBottom: '1px solid #f5f5f5' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#888', width: '16px' }}>{i + 1}</span>
                <div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{c.name}</div>
                  {c.company && <div style={{ fontSize: '0.7rem', color: '#888' }}>{c.company}</div>}
                </div>
              </div>
              <span style={{ fontSize: '0.65rem', color: c.status === 'activated' ? '#00712D' : '#aaa', fontWeight: 600 }}>
                {c.status === 'activated' ? '✓ Active' : c.status === 'in_progress' ? '⏳' : '—'}
              </span>
            </div>
          ))
        )}
      </Section>

      <Section title="Target Companies" icon="🏢" filled={!!profile?.target_companies} onEdit={() => onNavigate('day8')}>
        {profile?.target_companies ? (
          <div style={{ fontSize: '0.82rem', color: '#1A1A1A', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{profile.target_companies}</div>
        ) : (
          <p style={{ color: '#aaa', fontSize: '0.82rem', fontStyle: 'italic' }}>Not started yet. Day 8 walks you through building your target company list of 25.</p>
        )}
      </Section>

      <Section title="STAR Stories" icon="💼" filled={false} onEdit={() => onNavigate('chat')}>
        <p style={{ color: '#555', fontSize: '0.82rem', lineHeight: 1.6 }}>Your four STAR stories are built in Day 28. Use the AI Coach to develop and practice them before any interview.</p>
        <button onClick={() => onNavigate('chat')} className="btn-secondary" style={{ width: '100%', marginTop: '0.5rem', padding: '0.6rem', fontSize: '0.82rem' }}>Build with AI Coach →</button>
      </Section>

      {/* I got hired modal */}
      {showHired && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '2.5rem 2rem', textAlign: 'center', maxWidth: '360px', width: '100%' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: 700, color: '#00712D', marginBottom: '0.75rem' }}>You got the job!</div>
            <p style={{ color: '#555', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '1.5rem' }}>
              Close the loop. Call your references, thank your Hot Ten, and tell your Inner Circle. And when you're settled — be someone else's warm path.
            </p>
            <button className="btn-primary" onClick={() => { setShowHired(false); onHired && onHired() }} style={{ width: '100%', marginBottom: '0.75rem' }}>
              Download my complete workbook →
            </button>
            <div style={{ marginBottom: '0.75rem' }}>
              <CalendarExport session={session} />
            </div>
            <button className="btn-ghost" onClick={() => setShowHired(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  )
}
